import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Output,
  signal,
  ViewChild,
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { NgZone } from '@angular/core';
import { NotificationService } from 'src/app/shared/services/notification.service';

@Component({
    selector: 'que-document-camera',
    imports: [CommonModule, TranslateDirective, TranslatePipe],
    templateUrl: './document-camera.component.html',
    styleUrls: ['./document-camera.component.scss']
})
export class DocumentCameraComponent {
  @ViewChild('video') videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  @Output() fileCaptured = new EventEmitter<File>();

  stream: MediaStream | null = null;
  capturedImageUrl = signal<string | null>(null);
  capturedFile: File | null = null;
  isCameraOpen = signal(false);
  isModalOpen = false;
  facingMode = signal<'environment' | 'user'>('environment');
  zone = inject(NgZone);
  #notification = inject(NotificationService);

  openModal(): void {
    if (this.isModalOpen) return;

    this.isModalOpen = true;
    this.capturedImageUrl.set(null);
    this.capturedFile = null;
    document.body.style.overflow = 'hidden';
    console.log('file:open modal');

    this.openCamera();
  }

  closeModal(): void {
    this.closeCamera();
    this.capturedImageUrl.set(null);
    this.capturedFile = null;
    this.isModalOpen = false;
    document.body.style.overflow = '';
  }

  async openCamera(): Promise<void> {
    // Clear preview state immediately so UI doesn't show stale data
    console.log('file:open camera');
    this.capturedImageUrl.set(null);
    this.capturedFile = null;

    try {
      this.closeCamera();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: this.facingMode() },
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 15 },
        },
        audio: false,
      });

      // Guard: modal may have been closed while awaiting getUserMedia
      if (!this.isModalOpen || !this.videoRef?.nativeElement) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      this.stream = stream;

      const video = this.videoRef.nativeElement;
      video.srcObject = stream;

      await video.play();

      // Guard again after video.play()
      if (!this.isModalOpen) {
        this.closeCamera();
        return;
      }

      this.isCameraOpen.set(true);
      console.log('file:', this.isCameraOpen);

    } catch (error: any) {
      console.error('Camera error name:', error?.name);
      console.error('Camera error message:', error?.message);
      console.error('Full camera error:', error);

      this.#notification.showError({
        en: `${error?.name}: ${error?.message}`,
        he: `${error?.name}: ${error?.message}`,
      });
    }
  }

  switchCamera(): void {
    this.facingMode.set(this.facingMode() === 'environment' ? 'user' : 'environment');
    this.openCamera();
  }

  captureDocument(): void {
    const video = this.videoRef.nativeElement;
    const canvas = this.canvasRef.nativeElement;
    if (!video || !canvas) {
      this.#notification.showError({
        en: 'Camera is not ready yet. Please try again in a moment.',
        he: 'המצלמה עדיין לא נטענה. נסה שוב בעוד רגע.',
      });
      return;
    }
    if (!video.videoWidth || !video.videoHeight) {
      this.#notification.showError({
        en: 'Camera is not ready yet. Please try again in a moment.',
        he: 'המצלמה עדיין לא נטענה. נסה שוב בעוד רגע.',
      });
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');

    if (!context) {
      this.#notification.showError({
        en: 'Cannot perform capture.',
        he: 'לא ניתן לבצע צילום.',
      });
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          this.#notification.showError({
            en: 'Cannot create a file from the capture.',
            he: 'לא ניתן ליצור קובץ מהצילום.',
          });
          return;
        }

        const fileName = `document_${new Date().getTime()}.jpg`;

        this.zone.run(() => {
          this.capturedFile = new File([blob], fileName, {
            type: 'image/jpeg',
          });
          this.capturedImageUrl.set(URL.createObjectURL(blob));
          console.log('Captured file:', this.capturedFile);
          if (this.stream) {
            this.stream.getTracks().forEach((track) => track.stop());
            this.stream = null;
          }
          this.isCameraOpen.set(false);
        });
      },
      'image/jpeg',
      0.92,
    );
  }

  addCapturedFileToUpload(): void {
    if (!this.capturedFile) {
      this.#notification.showError({
        en: 'You must capture a document before adding.',
        he: 'יש לצלם מסמך לפני הוספה.',
      });
      return;
    }

    const fileToEmit = this.capturedFile;
    this.closeModal();
    this.fileCaptured.emit(fileToEmit);
  }

  closeCamera(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    if (this.videoRef?.nativeElement) {
      this.videoRef.nativeElement.pause();
      this.videoRef.nativeElement.srcObject = null;
    }

    this.isCameraOpen.set(false);
  }
}